import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createInviteSchema, type CreateInviteInput } from '@proref/schemas';
import { PageHeader } from '../../components/shared/PageHeader';
import { RouteTabs } from '../../components/shared/RouteTabs';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Field } from '../../components/shared/Field';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { apiErrorMessage } from '../../lib/api';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { useCreateInvite, useInvitesQuery, useResendInvite } from '../../hooks/use-invites';
import { CopyLink } from '../../components/admin/CopyLink';
import { BulkInviteDialog } from '../../components/admin/BulkInviteDialog';
import type { CreatedInvite, InviteListItem } from '../../services/invites.service';

type InviteStatus = 'pending' | 'used' | 'expired';

function statusOf(inv: InviteListItem): InviteStatus {
  if (inv.used_at) return 'used';
  if (new Date(inv.expires_at) < new Date()) return 'expired';
  return 'pending';
}

const STATUS_CHIP: Record<InviteStatus, string> = {
  pending: 'bg-accent-tint text-accent',
  used: 'bg-success-tint text-success',
  expired: 'bg-surface-2 text-muted',
};

// A function (not a static map) since the label is translated.
function statusLabel(t: TFunction, status: InviteStatus): string {
  return t(`admin.users.invitesPage.status${status.charAt(0).toUpperCase()}${status.slice(1)}`);
}

function CreatedPanel({ invite, emailSent }: { invite: CreatedInvite; emailSent: boolean }) {
  const { t } = useTranslation();
  const url = `${window.location.origin}/signup?token=${invite.token}`;
  // When the email went out, the link panel is a convenience copy; when it didn't, the panel is
  // the ONLY way the invitee gets in — so the styling + copy escalate to a warning.
  const tone = emailSent ? 'border-success/40 bg-success/10' : 'border-warn/50 bg-warn/10';
  return (
    <div className={'rounded border p-4 ' + tone}>
      <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-ink">
        <Icon.check className={'w-4 h-4 ' + (emailSent ? 'text-success' : 'text-warn')} />
        {t('admin.users.invitesPage.createdHeading', { name: invite.full_name })}
      </div>
      <p className="mb-2.5 text-[12.5px] text-muted">
        {emailSent
          ? t('admin.users.invitesPage.emailSentNote', { email: invite.email })
          : t('admin.users.invitesPage.emailFailedNote', { email: invite.email })}
        {t('admin.users.invitesPage.onlyShownOnce', {
          date: new Date(invite.expires_at).toLocaleDateString(),
        })}
      </p>
      <CopyLink url={url} />
    </div>
  );
}

// Resend control for a single pending invite row — re-sends the email and reports the outcome.
// A warning (not a success) when delivery was skipped keeps the silent-failure honest.
function ResendButton({ invite }: { invite: InviteListItem }) {
  const { t } = useTranslation();
  const resend = useResendInvite();
  async function onResend() {
    try {
      const result = await resend.mutateAsync(invite.id);
      if (result.emailSent) {
        notifySuccess(t('admin.users.invitesPage.resentToast', { email: result.email }));
      } else {
        notifyWarning(t('admin.users.invitesPage.resentButFailedToast', { email: result.email }));
      }
    } catch (err) {
      notifyError(err, t('admin.users.invitesPage.resendErrorToast'));
    }
  }
  return (
    <Button variant="default" size="sm" onClick={onResend} disabled={resend.isPending}>
      {resend.isPending ? (
        <Spinner className="w-3.5 h-3.5" />
      ) : (
        <>
          <Icon.send className="w-3.5 h-3.5" /> {t('admin.users.invitesPage.resend')}
        </>
      )}
    </Button>
  );
}

function InviteList() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useInvitesQuery();

  // First load only (no cached data yet); background refetches show nothing here.
  if (isLoading) {
    return <ListSkeleton />;
  }

  // Only blank to the error state when there's nothing cached to fall back on — a failed
  // background refetch keeps the last-good invite list on screen.
  if (isError && !data) {
    return (
      <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
        {apiErrorMessage(error, t('admin.users.invitesPage.couldNotLoad'))}
      </div>
    );
  }

  const invites = data ?? [];
  if (invites.length === 0) {
    return (
      <div className="grid place-items-center rounded border border-dashed border-line-2 bg-bg-2 px-6 py-12 text-center">
        <p className="text-sm text-muted">{t('admin.users.invitesPage.noInvitesYet')}</p>
      </div>
    );
  }

  return (
    // overflow-x-auto (not -hidden): six columns including an actions cell — the widest table in
    // the admin set, so it scrolls on anything narrower than a laptop.
    <div className="overflow-x-auto rounded border border-line bg-surface">
      <table className="w-full min-w-[760px] text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
            <th className="px-4 py-2.5 font-semibold">{t('admin.users.invitesPage.colName')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.users.inviteDialog.emailLabel')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colRole')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.exams.list.colStatus')}</th>
            <th className="px-4 py-2.5 font-semibold">{t('admin.users.invitesPage.colExpires')}</th>
            <th className="px-4 py-2.5 text-right font-semibold">{t('admin.users.federationRoster.colActions')}</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((inv) => {
            const status = statusOf(inv);
            return (
              <tr key={inv.id} className="border-b border-line last:border-0">
                <td className="px-4 py-2.5 font-medium text-ink">{inv.full_name}</td>
                <td className="px-4 py-2.5 text-ink-2">{inv.email}</td>
                <td className="px-4 py-2.5 text-ink-2">
                  {inv.role === 'referee' || inv.role === 'federation_admin' || inv.role === 'super_admin'
                    ? t(`admin.users.roleLabel.${inv.role}`)
                    : inv.role}
                </td>
                <td className="px-4 py-2.5">
                  <span className={'inline-flex h-[22px] items-center rounded-full px-2.5 text-[11px] font-medium capitalize ' + STATUS_CHIP[status]}>
                    {statusLabel(t, status)}
                  </span>
                </td>
                <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
                  {new Date(inv.expires_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-2.5 text-right">
                  {/* Resend only makes sense while the invite is still redeemable. */}
                  {status === 'pending' || status === 'expired' ? <ResendButton invite={inv} /> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function InvitesPage() {
  const { t } = useTranslation();
  const createInvite = useCreateInvite();
  const [created, setCreated] = useState<{ invite: CreatedInvite; emailSent: boolean } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateInviteInput>({
    resolver: zodResolver(createInviteSchema),
    defaultValues: { full_name: '', email: '', role: 'referee' },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      // federation_admin: full_name + email + role travel; the server derives federation/sport
      // from the JWT. full_name is the admin's identification of the recipient — the signup
      // form never asks for it, so this is the canonical entry point for that field.
      const { invite, emailSent } = await createInvite.mutateAsync({
        full_name: values.full_name,
        email: values.email,
        role: values.role,
      });
      setCreated({ invite, emailSent });
      // Distinguish "sent" from "created but not emailed" so a silent delivery miss is visible.
      if (emailSent) {
        notifySuccess(t('admin.users.inviteDialog.sentToast', { name: invite.full_name }));
      } else {
        notifyWarning(t('admin.users.inviteDialog.sentButEmailFailedToast', { name: invite.full_name }));
      }
      reset({ full_name: '', email: '', role: values.role });
    } catch (err) {
      notifyError(err, t('admin.users.inviteDialog.errorToast'));
    }
  });

  return (
    <>
      <PageHeader
        title={t('admin.users.list.title')}
        sub={t('admin.users.invitesPage.sub')}
        actions={<BulkInviteDialog />}
      />

      {/* Onboarding (this page) vs. the existing roster + removal — separate routes rather than
          local tab state, same reasoning as ExamTabs: an admin can link a colleague straight to
          "Users" and the back button behaves. */}
      <RouteTabs
        tabs={[
          { to: '/invites', label: t('admin.users.federationRoster.tabInvite'), end: true },
          { to: '/invites/users', label: t('admin.users.federationRoster.tabUsers') },
        ]}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Create form */}
        <div className="rounded border border-line bg-surface p-5 shadow-1">
          <div className="mb-4 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-sm bg-accent-tint text-accent">
              <Icon.users className="w-4 h-4" />
            </div>
            <h2 className="text-[14px] font-semibold text-ink">{t('admin.users.invitesPage.formHeading')}</h2>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
            <Field label={t('admin.users.editDialog.fullNameLabel')} htmlFor="full_name" error={errors.full_name?.message}>
              <Input
                id="full_name"
                autoComplete="off"
                placeholder={t('admin.users.inviteDialog.fullNamePlaceholder')}
                {...register('full_name')}
              />
              {/* Sub-hint sits below the input — the signup form deliberately doesn't
                  collect a name, so admins are the single source of truth for it. */}
              <p className="mt-1 text-[11px] text-muted">
                {t('admin.users.invitesPage.fullNameHint')}
              </p>
            </Field>
            <Field label={t('admin.users.inviteDialog.emailLabel')} htmlFor="email" error={errors.email?.message}>
              <Input
                id="email"
                type="email"
                placeholder={t('admin.users.inviteDialog.emailPlaceholder')}
                {...register('email')}
              />
            </Field>
            <Field label={t('admin.users.editDialog.roleLabel')} htmlFor="role" error={errors.role?.message}>
              <select
                id="role"
                className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
                {...register('role')}
              >
                <option value="referee">{t('admin.users.editDialog.roleReferee')}</option>
                <option value="federation_admin">{t('admin.users.editDialog.roleFederationAdmin')}</option>
              </select>
            </Field>
            <Button type="submit" variant="accent" disabled={createInvite.isPending} className="mt-1 self-start">
              {createInvite.isPending ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <>
                  {t('admin.users.invitesPage.createInvite')} <Icon.plus className="w-3.5 h-3.5" />
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Last created invite + copyable link */}
        <div>
          {created ? (
            <CreatedPanel invite={created.invite} emailSent={created.emailSent} />
          ) : (
            <div className="flex h-full items-center justify-center rounded border border-dashed border-line-2 bg-bg-2 p-6 text-center">
              <p className="max-w-xs text-[13px] text-muted">
                {t('admin.users.invitesPage.noInviteYet')}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-7">
        <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-[0.05em] text-muted">{t('admin.users.invitesPage.invitesHeading')}</h2>
        <InviteList />
      </div>
    </>
  );
}
